import { useCallback, useRef, useState } from 'react';
import api from '../config/api';

/**
 * Shared itinerary PDF download with progress for the export dialog.
 * Server work is slow before bytes arrive, so progress creeps while waiting
 * and then tracks the download when Content-Length is available.
 */
export default function useTripPdfExport() {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('preparing');
  const tickRef = useRef(null);

  const clearTick = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const resetSoon = useCallback(() => {
    window.setTimeout(() => {
      setOpen(false);
      setProgress(0);
      setPhase('preparing');
    }, 450);
  }, []);

  const downloadTripPdf = useCallback(async ({
    tripId,
    tripTitle,
    usePartnerAuth = false,
    params = undefined,
  }) => {
    if (!tripId) return;
    clearTick();
    setOpen(true);
    setPhase('preparing');
    setProgress(6);
    tickRef.current = setInterval(() => {
      setProgress((current) => {
        if (current >= 84) return current;
        const step = current < 40 ? 3 : current < 70 ? 2 : 1;
        return Math.min(84, current + step);
      });
    }, 700);

    try {
      const response = await api.get(`/pdf/trip/${tripId}`, {
        responseType: 'blob',
        timeout: 180000,
        ...(params ? { params } : {}),
        ...(usePartnerAuth ? { usePartnerAuth: true } : {}),
        onDownloadProgress: (event) => {
          if (!event.total) return;
          clearTick();
          setPhase('downloading');
          const ratio = Math.min(1, event.loaded / event.total);
          setProgress(84 + Math.round(ratio * 15));
        },
      });

      const contentType = String(response.headers?.['content-type'] || '');
      if (contentType.includes('application/json') || response.data?.type === 'application/json') {
        const text = await response.data.text();
        let message = 'Failed to download PDF';
        try {
          const parsed = JSON.parse(text);
          if (parsed?.error) message = parsed.error;
        } catch {
          // keep default
        }
        const error = new Error(message);
        error.response = { data: { error: message } };
        throw error;
      }

      clearTick();
      setPhase('done');
      setProgress(100);

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeTitle = String(tripTitle || 'trip').replace(/[^a-zA-Z0-9]+/g, '_');
      link.download = `${safeTitle}_itinerary.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      resetSoon();
      return { ok: true };
    } catch (error) {
      clearTick();
      setOpen(false);
      setProgress(0);
      setPhase('preparing');
      throw error;
    }
  }, [resetSoon]);

  return {
    downloadTripPdf,
    pdfProgressOpen: open,
    pdfProgress: progress,
    pdfProgressPhase: phase,
  };
}
